import { createElement, lazy, type ComponentType } from 'react';
import { Users } from 'lucide-react';
import type { FeatureModule, Navigation } from '@/app/contract';
import { APP_ROUTES, requestsPath, organizationsPath, myCodePath } from '@/app/paths';
import { registerNamespace } from '@/i18n';
import { AddFriend } from './public';
import en from './locales/en.json';
import zh from './locales/zh-Hans.json';
import './people.css';
registerNamespace('people', {
  en,
  'zh-Hans': zh
});
// RouteView adds its own suspense boundary and expects a callable component.
function page(loader: () => Promise<{
  default: ComponentType;
}>) {
  const Screen = lazy(loader);
  return function PeopleRoute() {
    return createElement(Screen);
  };
}
const People = page(() => import('./people-screen'));
const Requests = page(() => import('./routes').then(m => ({
  default: m.RequestsRoute
})));
const Profile = page(() => import('./routes').then(m => ({
  default: m.PersonRoute
})));
const Groups = page(() => import('./directories').then(m => ({
  default: m.GroupDirectoryScreen
})));
const Official = page(() => import('./directories').then(m => ({
  default: m.OfficialScreen
})));
const LinkedProfile = page(() => import('./directories').then(m => ({
  default: m.LinkedProfileScreen
})));
const Blocked = page(() => import('./directories').then(m => ({
  default: m.BlockedScreen
})));
const Organizations = page(() => import('./organizations').then(m => ({
  default: m.OrganizationsScreen
})));
const Organization = page(() => import('./organizations').then(m => ({
  default: m.OrganizationScreen
})));
const ContactLinks = page(() => import('./links').then(m => ({
  default: m.ContactLinksScreen
})));
const ContactLinkUses = page(() => import('./links').then(m => ({
  default: m.ContactLinkUsesScreen
})));
const MyCodePage = page(() => import('./routes').then(m => ({
  default: m.MyCodeRoute
})));
const Collections = page(() => import('./routes').then(m => ({
  default: m.CollectionsRoute
})));
const Collection = page(() => import('./routes').then(m => ({
  default: m.CollectionRoute
})));
function addFriend(nav: Navigation) {
  let dismiss = () => undefined as void;
  const handle = nav.present(createElement(AddFriend, {
    onClose: () => dismiss()
  }), {
    size: 'large'
  });
  dismiss = handle.dismiss;
}
const feature: FeatureModule = {
  id: 'people',
  section: {
    id: 'people',
    titleKey: 'people:title',
    icon: Users
  },
  routes: [{
    ...APP_ROUTES.people,
    component: People
  }, {
    ...APP_ROUTES.peopleRequests,
    component: Requests
  }, {
    ...APP_ROUTES.peopleGroups,
    component: Groups
  }, {
    ...APP_ROUTES.officialAccounts,
    component: Official
  }, {
    ...APP_ROUTES.linkedProfile,
    component: LinkedProfile
  }, {
    ...APP_ROUTES.person,
    component: Profile
  }, {
    ...APP_ROUTES.blocked,
    component: Blocked
  }, {
    ...APP_ROUTES.organizations,
    component: Organizations
  }, {
    ...APP_ROUTES.organization,
    component: Organization
  }, {
    ...APP_ROUTES.contactLinks,
    component: ContactLinks
  }, {
    ...APP_ROUTES.contactLinkUses,
    component: ContactLinkUses
  }, {
    ...APP_ROUTES.myCode,
    component: MyCodePage
  }, {
    ...APP_ROUTES.collections,
    component: Collections
  }, {
    ...APP_ROUTES.collection,
    component: Collection
  }],
  commands: [{
    id: 'people.addFriend',
    titleKey: 'people:add.title',
    run: addFriend
  }, {
    id: 'people.requests',
    titleKey: 'people:requests.title',
    run: nav => nav.open(requestsPath())
  }, {
    id: 'people.organizations',
    titleKey: 'people:orgs.title',
    run: nav => nav.open(organizationsPath(nav.section))
  }, {
    id: 'people.myCode',
    titleKey: 'people:web.myLink',
    run: nav => nav.open(myCodePath(nav.section))
  }]
};
export default feature;
export { AddFriend, AcceptFriendRequest, MyCode, usePresence, type AddFriendProps, type AcceptFriendRequestProps, type MyCodeProps, type PresenceInfo } from './public';
